import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, Cell, LabelList } from 'recharts';
import styles from './ProposalComparisonView.module.css';
import { proposalComparisonService } from '../../services/api';
import Toast from '../../components/common/Toast/Toast';
import AdSenseAd from '../../components/ads/AdSenseAd/AdSenseAd';
import MockAd from '../../components/ads/MockAd/MockAd';

const ProposalComparisonView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGuestUser = searchParams.get('guest') === 'true';
  
  const [activeTab, setActiveTab] = useState('preview');
  const [selectedProposalIndex, setSelectedProposalIndex] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);
  const [activeContractors, setActiveContractors] = useState(null);
  const [chartType, setChartType] = useState('radar');
  const [savedComparisonData, setSavedComparisonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // New state for file management
  const [comparisonId, setComparisonId] = useState(id);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [contractorFiles, setContractorFiles] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(false);
  // Add state for project comparison data
  const [projectComparisonData, setProjectComparisonData] = useState(null);
  // Add state for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch the comparison data when the component mounts
  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // If we have an ID, fetch the data from the backend
        if (id) {
          // Use guest-aware API call
          const response = await proposalComparisonService.getComparisonById(id, isGuestUser);
          
          if (response.data && response.data.status === 'success') {
            setSavedComparisonData(response.data.data);
            setComparisonId(response.data.data.id);
            
            // For guest users, we'll unlock premium features automatically
            if (isGuestUser || response.data.data.isGuestProject) {
              console.log('Unlocking premium features for guest/migrated project');
              setIsPremiumUnlocked(true);
            }
            
            // Get user ID and project name for fetching project-specific comparison data
            const userId = response.data.data.user_id;
            const projectName = response.data.data.project_name;
            
            // Only fetch project-specific data for authenticated users
            if (userId && projectName && !isGuestUser && !response.data.data.isGuestProject) {
              try {
                // Fetch project-specific comparison data
                const projectDataResponse = await proposalComparisonService.getProjectComparisonData(
                  userId, 
                  projectName
                );
                
                if (projectDataResponse.data && projectDataResponse.data.status === 'success') {
                  setProjectComparisonData(projectDataResponse.data.data);
                  
                  // Initialize activeContractors from project-specific data
                  const contractorNames = Object.keys(projectDataResponse.data.data.comparison.scorecard)
                    .filter(key => key !== 'categories');
                    
                  setActiveContractors(contractorNames.reduce((acc, name) => {
                    acc[name] = true;
                    return acc;
                  }, {}));
                }
              } catch (projectDataErr) {
                console.error("Error fetching project comparison data:", projectDataErr);
                setToast({
                  type: 'error',
                  message: 'Error loading project-specific comparison data'
                });
                
                // Fall back to using the saved data if available
                if (response.data.data.comparison_data) {
                  // Use the saved data
                  const parsedData = typeof response.data.data.comparison_data === 'string' 
                    ? JSON.parse(response.data.data.comparison_data) 
                    : response.data.data.comparison_data;
                  
                  setProjectComparisonData(parsedData);
                  
                  // Initialize activeContractors based on the fallback data
                  const contractorNames = Object.keys(parsedData.comparison.scorecard)
                    .filter(key => key !== 'categories');
                    
                  setActiveContractors(contractorNames.reduce((acc, name) => {
                    acc[name] = true;
                    return acc;
                  }, {}));
                }
              }
            } else if (response.data.data.comparison_data) {
              // Use the saved data for guest users or when no userId/projectName is available
              const parsedData = typeof response.data.data.comparison_data === 'string' 
                ? JSON.parse(response.data.data.comparison_data) 
                : response.data.data.comparison_data;
              
              setProjectComparisonData(parsedData);
              
              // Initialize activeContractors based on the saved data
              const contractorNames = Object.keys(parsedData.comparison.scorecard)
                .filter(key => key !== 'categories');
                
              setActiveContractors(contractorNames.reduce((acc, name) => {
                acc[name] = true;
                return acc;
              }, {}));
            } else {
              // For guest users without saved comparison data, generate basic structure
              if (isGuestUser || response.data.data.isGuestProject) {
                // Initialize with basic contractor data from the comparison
                const contractors = response.data.data.contractors || [];
                const contractorNames = contractors.map(c => c.name || 'Unknown Contractor');
                
                setActiveContractors(contractorNames.reduce((acc, name) => {
                  acc[name] = true;
                  return acc;
                }, {}));
                
                // Set basic comparison data structure
                setProjectComparisonData({
                  comparison: {
                    scorecard: {
                      categories: [],
                      ...contractorNames.reduce((acc, name) => {
                        acc[name] = {};
                        return acc;
                      }, {})
                    }
                  }
                });
              }
            }
            
            // Fetch files for each contractor
            await fetchContractorFiles(response.data.data.id);
          } else {
            console.error('Unexpected response format:', response.data);
            throw new Error('Invalid response format from server');
          }
        }
      } catch (err) {
        setError("Failed to load comparison data. Please try again.");
        setToast({
          type: 'error',
          message: 'Error loading comparison data'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisonData();
  }, [id, isGuestUser]);
  
  // Fetch files for each contractor
  const fetchContractorFiles = async (comparisonId) => {
    try {
      // Get all files for this comparison
      const response = await proposalComparisonService.getComparisonFiles(comparisonId);
      
      if (response.data && response.data.status === 'success') {
        // Organize files by contractor
        const filesByContractor = {};
        
        response.data.data.files.forEach(file => {
          if (!filesByContractor[file.contractor]) {
            filesByContractor[file.contractor] = [];
          }
          filesByContractor[file.contractor].push(file);
        });
        
        setContractorFiles(filesByContractor);
      }
    } catch (err) {
      console.error("Error fetching files:", err);
      setToast({
        type: 'error',
        message: 'Error loading contractor files'
      });
    }
  };

  // Toggle contractor visibility
  const toggleContractor = (name) => {
    setActiveContractors(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Get filtered contractors (only those that are active)
  const getActiveContractorsList = () => {
    if (!activeContractors) return [];
    return Object.keys(activeContractors).filter(name => activeContractors[name]);
  };

  // Prepare data for bar chart representation of categories
  const prepareCategoryBarData = () => {
    if (!activeContractors || !projectComparisonData) return [];
    
    const activeList = getActiveContractorsList();
    const { categories } = projectComparisonData.comparison.scorecard;
    
    return categories.map(category => {
      const dataPoint = { category };
      activeList.forEach(name => {
        const index = categories.indexOf(category);
        dataPoint[name] = projectComparisonData.comparison.scorecard[name][index];
      });
      return dataPoint;
    });
  };

  // Premium plans
  const premiumPlans = [
    {
      id: 'comparison',
      name: 'Side-by-Side Comparison Report',
      price: 29.99,
      features: [
        'Detailed contractor comparison',
        'Pros & cons analysis',
        'Allowances breakdown',
        'Exclusions analysis'
      ],
      icon: 'fa-file-contract'
    },
    {
      id: 'questions',
      name: 'Clarification Questions Package',
      price: 14.99,
      features: [
        'Custom questions for contractors',
        'Categorized by project area',
        'Printable question sheet',
        'Follow-up templates'
      ],
      icon: 'fa-question-circle'
    },
    {
      id: 'bundle',
      name: 'Complete Bundle',
      price: 49.99,
      features: [
        'Full comparison report',
        'All clarification questions',
        'Negotiation insights',
        'Two FREE future comparisons'
      ],
      icon: 'fa-star',
      popular: true
    }
  ];
  
  // Prepare data for the radar chart
  const prepareRadarData = () => {
    const { categories } = projectComparisonData.comparison.scorecard;
    const contractorNames = Object.keys(projectComparisonData.comparison.scorecard).filter(key => key !== 'categories');
    
    return categories.map((category, index) => {
      const dataPoint = { category };
      contractorNames.forEach(name => {
        dataPoint[name] = projectComparisonData.comparison.scorecard[name][index];
      });
      return dataPoint;
    });
  };
  
  // Prepare bar chart data for cost comparison
  const prepareCostData = () => {
    return projectComparisonData.proposals.map(proposal => ({
      name: proposal.name,
      cost: proposal.total_cost,
    }));
  };

  // Prepare score data
  const prepareScoreData = () => {
    const scores = Object.entries(projectComparisonData.comparison.total_scores)
      .map(([name, score]) => ({ name, score }));
    
    return scores;
  };

  // Detect outliers in pricing
  const detectOutliers = () => {
    const costs = projectComparisonData.proposals.map(p => p.total_cost);
    const avg = costs.reduce((acc, val) => acc + val, 0) / costs.length;
    const threshold = avg * 0.3; // 30% deviation threshold
    
    // Calculate more accurate deviation percentages
    const highPrice = Math.max(...costs);
    const lowPrice = Math.min(...costs);
    
    return projectComparisonData.proposals.map(p => {
      // Calculate accurate percentage difference
      let deviation = 0;
      
      if (p.total_cost === highPrice && highPrice !== lowPrice) {
        // How much higher the high price is compared to the low price
        deviation = ((highPrice - lowPrice) / lowPrice * 100).toFixed(1);
      } else if (p.total_cost === lowPrice && highPrice !== lowPrice) {
        // How much lower the low price is compared to the high price
        deviation = ((highPrice - lowPrice) / highPrice * 100).toFixed(1);
      }
      
      return {
        name: p.name,
        cost: p.total_cost,
        isOutlier: Math.abs(p.total_cost - avg) > threshold,
        deviation: deviation,
        isHigher: p.total_cost === highPrice && highPrice !== lowPrice
      };
    });
  };

  // Find missing elements in bids
  const findMissingElements = () => {
    const allDetails = new Set();
    
    // Collect all possible detail keys
    projectComparisonData.proposals.forEach(proposal => {
      Object.keys(proposal.details).forEach(key => {
        if (key !== 'allowances' && key !== 'exclusions') {
          allDetails.add(key);
        }
      });
    });
    
    // Check which elements are missing from each proposal
    return projectComparisonData.proposals.map(proposal => {
      const missing = [...allDetails].filter(key => !Object.keys(proposal.details).includes(key));
      return {
        name: proposal.name,
        missing: missing.map(m => m.replace(/_/g, ' '))
      };
    });
  };

  // Generate colors for different contractors dynamically instead of hardcoding
  const getContractorColors = () => {
    if (!projectComparisonData) return {};
    
    // Predefined color palette - use these colors in order for different contractors
    const colorPalette = ['#0077cc', '#ff9900', '#28a745', '#6f42c1', '#e83e8c', '#fd7e14'];
    
    // Get contractor names from the comparison data
    const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
      .filter(key => key !== 'categories');
    
    // Assign colors to each contractor
    const colors = {};
    contractorNames.forEach((name, index) => {
      colors[name] = colorPalette[index % colorPalette.length];
    });
    
    return colors;
  };

  // Colors for different contractors - now dynamic
  const contractorColors = getContractorColors();

  const getInitials = (name) => {
    return name.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const handleUnlock = () => {
    if (selectedPlan) {
      setIsPremiumUnlocked(true);
      setShowUpgradeModal(false);
      // Auto-navigate to full comparison tab
      setActiveTab('overview');
      
      // Do not automatically save comparison data
      // saveComparisonToBackend();
    }
  };
  
  // Save comparison data to the backend
  const saveComparisonToBackend = async () => {
    try {
      // Extract contractor names from the comparison data
      const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
        .filter(key => key !== 'categories');
      
      // Prepare data for the API request
      const requestData = {
        projectName: savedComparisonData?.project_name || projectComparisonData?.proposals?.[0]?.project_name || '', 
        comparisonData: projectComparisonData,
        contractors: contractorNames
      };
      
      // Only proceed if we have a valid project name
      if (!requestData.projectName) {
        setToast({
          type: 'error',
          message: 'Project name is required to save the comparison'
        });
        return;
      }
      
      // Call the API using the service
      const response = await proposalComparisonService.createComparison(requestData);
      
      console.log('Comparison saved successfully', response.data);
      
      // Set the comparison ID in state for file uploads
      const comparisonId = response.data.data.id;
      setComparisonId(comparisonId);
      
      setToast({
        type: 'success',
        message: 'Comparison saved successfully'
      });
    } catch (error) {
      console.error('Error saving comparison:', error);
      setToast({
        type: 'error',
        message: `Error saving comparison: ${error.message || 'Unknown error'}`
      });
    }
  };
  
  // Function to handle file upload for a contractor
  const handleFileUpload = async (files, contractor) => {
    if (!comparisonId) {
      console.error('No comparison ID available for upload');
      setToast({
        type: 'error',
        message: 'Unable to upload: No comparison ID available'
      });
      return;
    }
    
    if (!files || files.length === 0) {
      console.error('No files selected');
      setToast({
        type: 'error',
        message: 'Please select files to upload'
      });
      return;
    }
    
    setIsUploading(true);
    setUploadStatus(prev => ({
      ...prev,
      [contractor]: { status: 'uploading', progress: 0 }
    }));
    
    try {
      // Create a FormData object
      const formData = new FormData();
      
      // Add project name
      formData.append('projectName', savedComparisonData?.project_name || '');
      
      // Add contractor
      formData.append('contractor', contractor);
      
      // Add all files
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      // Upload using the service
      const response = await proposalComparisonService.uploadContractorFiles(
        comparisonId,
        contractor,
        formData
      );
      
      // Update state on success
      setUploadStatus(prev => ({
        ...prev,
        [contractor]: { status: 'success', progress: 100 }
      }));
      
      // Refresh the files list
      fetchContractorFiles(comparisonId);
      
      setToast({
        type: 'success',
        message: `Files uploaded successfully for ${contractor}`
      });
      
      console.log(`Files uploaded for ${contractor}:`, response.data);
    } catch (error) {
      console.error(`Error uploading files for ${contractor}:`, error);
      
      setUploadStatus(prev => ({
        ...prev,
        [contractor]: { status: 'error', message: error.message || 'Upload failed' }
      }));
      
      setToast({
        type: 'error',
        message: `Upload failed: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Function to handle file deletion
  const handleDeleteFile = async (fileId) => {
    try {
      const response = await proposalComparisonService.deleteFile(fileId);
      
      if (response.data && response.data.status === 'success') {
        // Refresh the files list
        fetchContractorFiles(comparisonId);
        
        setToast({
          type: 'success',
          message: 'File deleted successfully'
        });
      } else {
        throw new Error('Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      
      setToast({
        type: 'error',
        message: `Delete failed: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Function to handle viewing a file
  const handleViewFile = (file) => {
    setSelectedFile(file);
    setShowFilePreview(true);
  };
  
  // Function to close file preview
  const closeFilePreview = () => {
    setSelectedFile(null);
    setShowFilePreview(false);
  };
  
  // Get file URL for preview or download
  const getFileUrl = (file) => {
    // Base URL for the API
    const apiBaseUrl = process.env.NODE_ENV === 'production' 
      ? process.env.REACT_APP_API_URL.replace('/api', '')
      : 'http://localhost:5000';
      
    // Return the path to the file
    return `${apiBaseUrl}/${file.file_path.replace('backend/', '')}`;
  };

  // Render file preview modal
  const renderFilePreview = () => {
    if (!showFilePreview || !selectedFile) return null;
    
    const fileUrl = getFileUrl(selectedFile);
    const isImage = selectedFile.mime_type.includes('image');
    const isPDF = selectedFile.mime_type.includes('pdf');
    
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.filePreviewModal}>
          <div className={styles.filePreviewHeader}>
            <h2>{selectedFile.original_name}</h2>
            <button className={styles.closeModal} onClick={closeFilePreview}>×</button>
          </div>
          
          <div className={styles.filePreviewContent}>
            {isImage && (
              <img src={fileUrl} alt={selectedFile.original_name} className={styles.previewImage} />
            )}
            
            {isPDF && (
              <iframe 
                src={`${fileUrl}#view=FitH`} 
                title={selectedFile.original_name}
                className={styles.pdfPreview}
              />
            )}
            
            {!isImage && !isPDF && (
              <div className={styles.unsupportedPreview}>
                <i className="fas fa-file"></i>
                <p>Preview not available for this file type.</p>
              </div>
            )}
          </div>
          
          <div className={styles.filePreviewFooter}>
            <div className={styles.fileInfo}>
              <div className={styles.fileInfoItem}>
                <span>Size:</span> {(selectedFile.file_size / 1024).toFixed(2)} KB
              </div>
              <div className={styles.fileInfoItem}>
                <span>Type:</span> {selectedFile.mime_type}
              </div>
            </div>
            
            <div className={styles.filePreviewActions}>
              <a 
                href={fileUrl} 
                download={selectedFile.original_name}
                className={styles.downloadButton}
                target="_blank"
                rel="noreferrer"
              >
                <i className="fas fa-download"></i> Download
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render files tab
  const renderFilesTab = () => {
    return (
      <div className={styles.filesTab}>
        <div className={styles.filesHeader}>
          <h2>Files & Documents</h2>
          <p>Upload and manage contractor proposals, quotes, and supporting documents.</p>
        </div>
        
        <div className={styles.contractorsFilesList}>
          {Object.keys(activeContractors || {}).filter(name => activeContractors[name]).map(contractor => (
            <div key={contractor} className={styles.contractorFilesSection}>
              <div className={styles.contractorFileHeader}>
                <div 
                  className={styles.contractorAvatar}
                  style={{ backgroundColor: contractorColors[contractor] }}
                >
                  {getInitials(contractor)}
                </div>
                <h3>{contractor} Files</h3>
              </div>
              
              <div className={styles.filesContainer}>
                {contractorFiles[contractor] && contractorFiles[contractor].length > 0 ? (
                  <div className={styles.filesList}>
                    {contractorFiles[contractor].map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                        <div className={styles.fileIcon}>
                          <i className={
                            file.mime_type.includes('pdf') ? 'fas fa-file-pdf' :
                            file.mime_type.includes('image') ? 'fas fa-file-image' :
                            file.mime_type.includes('word') ? 'fas fa-file-word' :
                            file.mime_type.includes('excel') ? 'fas fa-file-excel' :
                            'fas fa-file'
                          }></i>
                        </div>
                        <div className={styles.fileDetails}>
                          <div className={styles.fileName} onClick={() => handleViewFile(file)}>
                            {file.original_name}
                          </div>
                          <div className={styles.fileInfo}>
                            <span>{(file.file_size / 1024).toFixed(2)} KB</span>
                            <span>•</span>
                            <span>{new Date(file.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className={styles.fileActions}>
                          <button 
                            className={styles.fileActionButton} 
                            onClick={() => handleViewFile(file)}
                            title="View File"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <a 
                            href={getFileUrl(file)} 
                            download={file.original_name}
                            className={styles.fileActionButton}
                            target="_blank"
                            rel="noreferrer"
                            title="Download File"
                          >
                            <i className="fas fa-download"></i>
                          </a>
                          <button 
                            className={`${styles.fileActionButton} ${styles.deleteButton}`} 
                            onClick={() => handleDeleteFile(file.id)}
                            title="Delete File"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyFiles}>
                    <div className={styles.emptyFilesIcon}>
                      <i className="fas fa-folder-open"></i>
                    </div>
                    <p>No files uploaded for this contractor yet.</p>
                  </div>
                )}
                
                <div className={styles.fileUploadSection}>
                  <div className={styles.uploadHeader}>
                    <h4>Upload New Files</h4>
                    <p>Add proposals, quotes or other documents for {contractor}.</p>
                  </div>
                  
                  <div className={styles.fileDropzone}>
                    <input
                      type="file"
                      id={`file-upload-${contractor}`}
                      className={styles.fileInput}
                      onChange={(e) => handleFileUpload(e.target.files, contractor)}
                      multiple
                    />
                    <label 
                      htmlFor={`file-upload-${contractor}`} 
                      className={styles.fileDropzoneLabel}
                    >
                      <div className={styles.dropzoneContent}>
                        <i className="fas fa-cloud-upload-alt"></i>
                        <p>Drag files here or click to browse</p>
                        <span>Accepts PDF, Word, Excel, and images</span>
                      </div>
                    </label>
                  </div>
                  
                  {uploadStatus[contractor] && (
                    <div 
                      className={`${styles.uploadStatus} ${
                        uploadStatus[contractor].status === 'uploading' ? styles.uploading :
                        uploadStatus[contractor].status === 'success' ? styles.success :
                        styles.error
                      }`}
                    >
                      {uploadStatus[contractor].status === 'uploading' && (
                        <>
                          <div className={styles.progressBar}>
                            <div 
                              className={styles.progressFill} 
                              style={{ width: `${uploadStatus[contractor].progress}%` }}
                            ></div>
                          </div>
                          <span>Uploading... {uploadStatus[contractor].progress}%</span>
                        </>
                      )}
                      
                      {uploadStatus[contractor].status === 'success' && (
                        <>
                          <i className="fas fa-check-circle"></i>
                          <span>Upload Complete</span>
                        </>
                      )}
                      
                      {uploadStatus[contractor].status === 'error' && (
                        <>
                          <i className="fas fa-exclamation-circle"></i>
                          <span>Error: {uploadStatus[contractor].message}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderPreviewTab = () => {
    const costData = prepareCostData();
    const outliers = detectOutliers();
    const missingElements = findMissingElements();
    
    // Calculate total allowances for each contractor
    const allowanceData = projectComparisonData.proposals.map(proposal => ({
      name: proposal.name,
      total: proposal.details.allowances ? 
        Object.values(proposal.details.allowances).reduce((sum, value) => sum + value, 0) : 0,
      count: proposal.details.allowances ? Object.keys(proposal.details.allowances).length : 0
    }));

    // Prepare sample allowance comparison data - make dynamic
    const prepareAllowanceComparisonData = () => {
      if (!projectComparisonData) return [];
      
      // Create categories that exist in either contractor's allowances
      const allCategories = new Set();
      projectComparisonData.proposals.forEach(proposal => {
        if (proposal.details.allowances) {
          Object.keys(proposal.details.allowances).forEach(key => {
            allCategories.add(key.replace(/_/g, ' '));
          });
        }
      });
      
      // Map allowances to standardized categories
      const standardizeCategory = (category) => {
        // Map similar categories to standardized name
        const mapping = {
          'windows_doors': 'windows',
          'windows': 'windows',
          'interior_doors': 'windows',
          'metal_roofing': 'roofing',
          'mirror_shower': 'bathroom',
          'shower_door': 'bathroom',
          'vanity_faucets': 'bathroom',
          'vanity': 'bathroom',
          'toilet': 'bathroom',
          'plumbing_fixtures': 'bathroom'
        };
        
        return mapping[category] || category;
      };
      
      // Instead of hardcoded keys, dynamically create entries for common allowances
      const commonAllowances = ['flooring', 'windows', 'tile'];
      
      return commonAllowances.map(category => {
        const dataPoint = { name: category };
        
        projectComparisonData.proposals.forEach(proposal => {
          const contractorName = proposal.name.split(' ')[0]; // Get the first part of the name
          
          // Find matching allowances for this category
          let value = 0;
          if (proposal.details.allowances) {
            Object.entries(proposal.details.allowances).forEach(([key, allowance]) => {
              const standardizedCategory = standardizeCategory(key);
              if (standardizedCategory.toLowerCase().includes(category.toLowerCase()) ||
                  key.toLowerCase().includes(category.toLowerCase())) {
                value += allowance;
              }
            });
          }
          
          dataPoint[contractorName] = value;
        });
        
        return dataPoint;
      });
    };

    // Get sample missing elements from each proposal
    const sampleMissingElements = {};
    missingElements.forEach(item => {
      if (item.missing.length > 0) {
        sampleMissingElements[item.name] = item.missing.slice(0, 3);
      }
    });
    
    // Get the recommendation but without the full reason
    const recommendedContractor = projectComparisonData.comparison.recommendation.best_choice;
    
    const handleOpenModal = () => {
      setShowUpgradeModal(true);
    };
    
    return (
      <div className={styles.previewTab}>
        <div className={styles.previewHeader}>
          <h2>Your Proposal Analysis Preview</h2>
          <p>We've analyzed your contractor proposals and found some interesting insights that could save you thousands.</p>
        </div>
        
        <div className={styles.recommendationPreview}>
          <div className={styles.recommendationHeaderPreview}>
            <i className="fas fa-award"></i>
            <h3>Recommended Contractor</h3>
          </div>
          <div className={styles.recommendationContentPreview}>
            <div className={styles.bestChoicePreview}>
              <strong>{recommendedContractor}</strong>
            </div>
            <p className={styles.upgradePrompt}>Unlock full analysis to see detailed reasoning</p>
            <button 
              className={styles.miniUnlockButton} 
              onClick={handleOpenModal}
              type="button"
            >
              <i className="fas fa-unlock"></i> See Why
            </button>
          </div>
        </div>
        
        <div className={styles.previewCards}>
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <i className="fas fa-chart-bar"></i>
              <h3>Cost Comparison</h3>
            </div>
            <div className={styles.previewChartContainer}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={costData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis unit="$" />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
                  <Bar dataKey="cost" fill="#0077cc" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className={styles.costBreakdownContainer}>
              <h4>Major Cost Differences:</h4>
              {(() => {
                // Get first contractor name for color
                const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
                  .filter(key => key !== 'categories');
                
                if (contractorNames.length === 0) return null;
                
                const primaryContractor = contractorNames[0];
                
                // Sample cost breakdown items
                const breakdownItems = [
                  { label: 'Labor', percentage: 75 },
                  { label: 'Materials', percentage: 40 },
                  { label: 'Permits', percentage: 20 }
                ];
                
                return breakdownItems.map((item, index) => (
                  <div key={index} className={styles.costBreakdownItem}>
                    <div className={styles.costBreakdownLabel}>{item.label}:</div>
                <div className={styles.costBreakdownBar}>
                  <div 
                    className={styles.costBreakdownFill} 
                        style={{
                          width: `${item.percentage}%`, 
                          backgroundColor: contractorColors[primaryContractor] || '#0077cc'
                        }}
                  ></div>
                </div>
                    <div className={styles.costBreakdownValue}>+{item.percentage}%</div>
              </div>
                ));
              })()}
            </div>
            
            <p className={styles.previewInsight}>
              <span className={styles.insightHighlight}>
                ${Math.abs(costData[0].cost - costData[1].cost).toLocaleString()} difference
              </span> between proposals. Costs range from <strong>${Math.min(...costData.map(d => d.cost)).toLocaleString()}</strong> to <strong>${Math.max(...costData.map(d => d.cost)).toLocaleString()}</strong>
            </p>
          </div>
          
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Price Outliers Detected</h3>
            </div>
            <div className={styles.outliersList}>
              {outliers.map((item, index) => (
                <div key={index} className={styles.outlierItem}>
                  <div className={styles.outlierName}>{item.name}</div>
                  <div className={styles.outlierDetail}>
                    <span className={styles.outlierPrice}>${item.cost.toLocaleString()}</span>
                    {item.isOutlier && (
                      <span className={item.isHigher ? styles.outlierHigh : styles.outlierLow}>
                        {item.isHigher ? '+' : '-'}{item.deviation}%
                      </span>
                    )}
                  </div>
                </div>
              ))}

              <div className={styles.outlierExplanation}>
                <h4>What this means:</h4>
                <ul className={styles.outlierExplanationList}>
                  <li>A deviation of +/- 30% from average is considered an outlier</li>
                  <li>A higher price may include more complete scope</li>
                  <li>A lower price may indicate missed elements</li>
                </ul>
              </div>
            </div>
            <p className={styles.previewInsight}>
              {outliers.some(o => o.isOutlier) 
                ? <span>Some proposals are <span className={styles.insightAlert}>significantly outside</span> the average price range.</span>
                : "All proposals are within a reasonable price range."}
            </p>
          </div>
          
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <i className="fas fa-clipboard-check"></i>
              <h3>Missing Elements</h3>
            </div>
            <div className={styles.missingList}>
              {missingElements.map((item, index) => (
                <div key={index} className={styles.missingItem}>
                  <div className={styles.missingName}>{item.name}</div>
                  <div className={styles.missingCount}>
                    {item.missing.length > 0 
                      ? <span className={styles.missingBadge}>{item.missing.length} missing</span>
                      : <span className={styles.completeBadge}>Complete</span>
                    }
                  </div>
                </div>
              ))}
            </div>
            
            {/* Sample of missing elements */}
            {Object.keys(sampleMissingElements).length > 0 && (
              <div className={styles.missingElementsPreview}>
                <h4>Examples of missing elements:</h4>
                {Object.entries(sampleMissingElements).map(([contractor, elements], index) => (
                  <div key={index} className={styles.missingElementsList}>
                    <strong>{contractor}:</strong> {elements.slice(0, 2).join(', ')}
                    {elements.length > 2 && <span> and {elements.length - 2} more...</span>}
                  </div>
                ))}
                <div className={styles.missingImpactMessage}>
                  <i className="fas fa-info-circle"></i>
                  <span>Missing elements can increase final costs by 10-25%</span>
                </div>
              </div>
            )}
            
            <p className={styles.previewInsight}>
              {missingElements.some(m => m.missing.length > 0)
                ? <span>Some proposals are <span className={styles.insightAlert}>missing key elements</span> that may affect your project.</span>
                : "All proposals include all standard elements."}
            </p>
          </div>
          
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <i className="fas fa-dollar-sign"></i>
              <h3>Allowances Breakdown</h3>
            </div>
            <div className={styles.allowanceChartContainer}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart 
                  data={prepareAllowanceComparisonData()} 
                  margin={{ top: 5, right: 20, bottom: 5, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis unit="$" />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                  <Legend />
                  {Object.keys(contractorColors).map(contractor => (
                    <Bar 
                      key={contractor} 
                      dataKey={contractor} 
                      fill={contractorColors[contractor]}
                      radius={[4, 4, 0, 0]} 
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className={styles.allowanceTotalsList}>
              {allowanceData.map((item, index) => (
                <div key={index} className={styles.allowanceTotalItem}>
                  <div className={styles.allowanceName}>
                    {item.name}
                    <span className={styles.allowanceCount}>{item.count} items</span>
                  </div>
                  <div className={styles.allowanceTotal}>
                    ${item.total.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <p className={styles.previewInsight}>
              <span className={styles.insightHighlight}>
                ${Math.abs(allowanceData[0].total - allowanceData[1].total).toLocaleString()} difference
              </span> in total allowances between contractors.
            </p>
          </div>
          
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <i className="fas fa-tasks"></i>
              <h3>Scope Comparison</h3>
            </div>
            <div className={styles.scopePreview}>
              {projectComparisonData.proposals.map((proposal, index) => (
                <div key={index} className={styles.scopeItem}>
                  <div className={styles.scopeHeader}>
                    <div className={styles.scopeContractor}>
                      <div 
                        className={styles.scopeAvatar} 
                        style={{backgroundColor: contractorColors[proposal.name.split(' ')[0]]}}
                      >
                        {getInitials(proposal.name)}
                      </div>
                      <h4>{proposal.name}</h4>
                    </div>
                    <div className={styles.scopePrice}>${proposal.total_cost.toLocaleString()}</div>
                  </div>
                  <p className={styles.scopeDescription}>
                    {proposal.scope}
                    {proposal.details.exclusions && 
                      <span className={styles.scopeHighlight}> <br/>Excludes: {proposal.details.exclusions.slice(0, 1).join(', ')}
                        {proposal.details.exclusions.length > 1 && '...'}
                      </span>
                    }
                  </p>
                  <div className={styles.scopeMorePrompt}>
                    <i className="fas fa-ellipsis-h"></i> 
                    <span>Unlock to see detailed scope breakdown</span>
                  </div>
                </div>
              ))}
            </div>
            <p className={styles.previewInsight}>
              <span className={styles.insightHighlight}>Key differences:</span> One contractor includes detailed planning while the other focuses on implementation details.
            </p>
          </div>
        </div>
        
        <div className={styles.previewCTA}>
          <h3>Ready to make the right decision?</h3>
          <p>Unlock the full analysis to see detailed comparisons, contractor strengths & weaknesses, and expert-curated questions to ask before hiring.</p>
          <div className={styles.ctaButtons}>
            <button 
              className={styles.unlockButton} 
              onClick={handleOpenModal}
              type="button"
            >
              <i className="fas fa-unlock"></i> Unlock Full Comparison
            </button>
            <div className={styles.ctaSavings}>
              <i className="fas fa-piggy-bank"></i>
              <span>Potentially save thousands with the right choice</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Prepare detailed score breakdown data for comparison
  const prepareDetailedScoreData = () => {
    const { categories } = projectComparisonData.comparison.scorecard;
    const contractorNames = Object.keys(projectComparisonData.comparison.scorecard).filter(key => key !== 'categories');
    
    return categories.map((category, index) => {
      const data = { category };
      contractorNames.forEach(name => {
        data[name] = projectComparisonData.comparison.scorecard[name][index];
      });
      return data;
    });
  };
  
  // Prepare timeline comparison data
  const prepareTimelineData = () => {
    if (!projectComparisonData) return [];
    
    // Get contractor names from the data
    const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
      .filter(key => key !== 'categories');
      
    // Extract phases from the actual project details
    const phases = new Set();
    
    // Get all phases from project details
    projectComparisonData.proposals.forEach(proposal => {
      Object.keys(proposal.details || {}).forEach(key => {
        // Skip allowances, exclusions, assumptions sections
        if (!['allowances', 'exclusions', 'assumptions'].includes(key)) {
          phases.add(key);
        }
      });
    });
    
    // Convert to array and create timeline items
    const timelinePhases = Array.from(phases).map(key => {
      // Format the phase name for display
      return { 
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        key
      };
    });
    
    // Sort phases in a somewhat logical construction order
    timelinePhases.sort((a, b) => {
      const order = [
        "planning", "design", "sitework", "demolition", "framing", 
        "roofing", "plumbing", "electric", "hvac", "insulation", 
        "walls", "drywall", "finish", "cleanup"
      ];
      
      const indexA = order.findIndex(o => a.key.includes(o));
      const indexB = order.findIndex(o => b.key.includes(o));
      
      // If both found in order list, sort by order
      if (indexA >= 0 && indexB >= 0) return indexA - indexB;
      // If only one found, the found one comes first
      if (indexA >= 0) return -1;
      if (indexB >= 0) return 1;
      // Otherwise sort alphabetically
      return a.name.localeCompare(b.name);
    });
    
    // Create timeline data with estimates based on description length and project complexity
    return timelinePhases.map(phase => {
      const timelineItem = { name: phase.name };
      
      // Assign duration to each contractor
      contractorNames.forEach((name, index) => {
        // Find the proposal for this contractor
        const proposal = projectComparisonData.proposals.find(p => 
          p.name.split(' ')[0] === name || p.name === name);
        
        // Base duration depends on description complexity
        let duration = 5; // Minimum 5 days
        
        if (proposal?.details[phase.key]) {
          const description = proposal.details[phase.key].toString();
          // Longer description likely means more complex work
          duration += Math.min(10, Math.floor(description.length / 20));
        }
        
        // Usually the more expensive contractor takes longer but is more thorough
        if (index === 0) {
          duration = Math.round(duration * 1.2); // 20% longer for first contractor
        }
        
        timelineItem[name] = duration;
      });
      
      return timelineItem;
    }).slice(0, 10); // Limit to 10 phases to avoid overloading the chart
  };
  
  // Prepare allowance breakdown comparison data
  const prepareAllowanceComparisonData = () => {
    // Create categories that exist in either contractor's allowances
    const allCategories = new Set();
    projectComparisonData.proposals.forEach(proposal => {
      if (proposal.details.allowances) {
        Object.keys(proposal.details.allowances).forEach(key => {
          allCategories.add(key.replace(/_/g, ' '));
        });
      }
    });
    
    // Map allowances to standardized categories
    const standardizeCategory = (category) => {
      // Map similar categories to standardized name
      const mapping = {
        'windows_doors': 'windows',
        'windows': 'windows',
        'interior_doors': 'windows',
        'metal_roofing': 'roofing',
        'mirror_shower': 'bathroom',
        'shower_door': 'bathroom',
        'vanity_faucets': 'bathroom',
        'vanity': 'bathroom',
        'toilet': 'bathroom',
        'plumbing_fixtures': 'bathroom'
      };
      
      return mapping[category] || category;
    };
    
    // Get allowance categories from actual data instead of hardcoded keys
    const allAllowances = new Set();
    projectComparisonData.proposals.forEach(proposal => {
      if (proposal.details.allowances) {
        Object.keys(proposal.details.allowances).forEach(key => {
          allAllowances.add(key.toLowerCase());
        });
      }
    });
    
    // Convert to array and limit to most relevant categories for display
    const commonAllowances = Array.from(allAllowances).slice(0, 5);
    
    return commonAllowances.map(category => {
      const dataPoint = { name: category };
      
      projectComparisonData.proposals.forEach(proposal => {
        const contractorName = proposal.name.split(' ')[0]; // Get the first part of the name
        
        // Find matching allowances for this category
        let value = 0;
        if (proposal.details.allowances) {
          Object.entries(proposal.details.allowances).forEach(([key, allowance]) => {
            const standardizedCategory = standardizeCategory(key);
            if (standardizedCategory.toLowerCase().includes(category.toLowerCase()) ||
                key.toLowerCase().includes(category.toLowerCase())) {
              value += allowance;
            }
          });
        }
        
        dataPoint[contractorName] = value;
      });
      
      return dataPoint;
    });
  };

  const renderOverviewTab = () => {
    const radarData = prepareRadarData();
    const costData = prepareCostData();
    const scoreData = prepareScoreData();
    const detailedScoreData = prepareDetailedScoreData();
    const timelineData = prepareTimelineData();
    const allowanceComparisonData = prepareAllowanceComparisonData();

    // If not premium and trying to access premium tab
    if (!isPremiumUnlocked) {
      return (
        <div className={styles.premiumBlocker}>
          <div className={styles.premiumBlockerContent}>
            <i className="fas fa-lock"></i>
            <h3>Premium Content</h3>
            <p>Unlock detailed comparisons and analysis to make the best decision for your project.</p>
            <button className={styles.unlockButton} onClick={() => setShowUpgradeModal(true)}>
              <i className="fas fa-unlock"></i> Unlock Full Comparison
            </button>
          </div>
        </div>
      );
    }

    // Calculate ROI and value metrics
    const highPrice = Math.max(...costData.map(d => d.cost));
    const lowPrice = Math.min(...costData.map(d => d.cost));
    const priceDifference = highPrice - lowPrice;
    
    // Get highest scored contractor
    const highestScore = Math.max(...Object.values(projectComparisonData.comparison.total_scores));
    const highestScoredContractor = Object.entries(projectComparisonData.comparison.total_scores)
      .find(([_, score]) => score === highestScore)?.[0];

    return (
      <div className={styles.overviewTab}>
        <div className={styles.overviewHeader}>
          <h2>Full Comparison Analysis</h2>
          <p>A comprehensive breakdown of contractor proposals, scoring, and recommendations to help you make an informed decision.</p>
        </div>
        
        <div className={styles.summarySection}>
          <div className={styles.summaryText}>
            <h3>Executive Summary</h3>
            <p>{projectComparisonData.comparison.summary}</p>
          </div>
          
          <div className={styles.summaryMetrics}>
            <div className={styles.metric}>
              <div className={styles.metricIcon}>
                <i className="fas fa-dollar-sign"></i>
              </div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>${priceDifference.toLocaleString()}</div>
                <div className={styles.metricLabel}>Price Difference</div>
              </div>
              <div className={styles.metricArrowWarn}>
                <i className="fas fa-arrow-up"></i>
                <span>Higher</span>
              </div>
            </div>

            <div className={styles.metric}>
              <div className={styles.metricIcon}>
                <i className="fas fa-star"></i>
              </div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>+{Math.abs(highestScore - Object.values(projectComparisonData.comparison.total_scores).find(score => score !== highestScore))}</div>
                <div className={styles.metricLabel}>Quality Score</div>
              </div>
              <div className={styles.metricArrow}>
                <i className="fas fa-arrow-up"></i>
                <span>Better</span>
              </div>
            </div>

            <div className={styles.metric}>
              <div className={styles.metricIcon}>
                <i className="fas fa-percentage"></i>
              </div>
              <div className={styles.metricContent}>
                <div className={styles.metricValue}>{Math.round((highPrice - lowPrice) / lowPrice * 100)}%</div>
                <div className={styles.metricLabel}>Price Variance</div>
              </div>
              <div className={styles.metricArrowWarn}>
                <i className="fas fa-exclamation-circle"></i>
                <span>Significant</span>
              </div>
            </div>
          </div>
          
          <div className={styles.recommendationBox}>
            <div className={styles.recommendationHeader}>
              <i className="fas fa-award"></i>
              <h3>Our Recommendation</h3>
            </div>
            <div className={styles.recommendationContent}>
              <div className={styles.bestChoice}>
                <span>Best Choice:</span> 
                <strong>{projectComparisonData.comparison.recommendation.best_choice}</strong>
              </div>
              <p>{projectComparisonData.comparison.recommendation.reason}</p>
              
              <div className={styles.recommendationTags}>
                <div className={styles.recommendationTag}>
                  <i className="fas fa-check-circle"></i>
                  <span>Quality Focused</span>
                </div>
                <div className={styles.recommendationTag}>
                  <i className="fas fa-shield-alt"></i>
                  <span>Better Warranty</span>
                </div>
                <div className={styles.recommendationTag}>
                  <i className="fas fa-file-contract"></i>
                  <span>Detailed Scope</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contextual Inline Ad - Home Improvement Related */}
        {/* Real AdSense Ad - Uncomment when ready */}
        {/* <AdSenseAd
          adSlot={process.env.REACT_APP_ADSENSE_INLINE_SLOT || "your-inline-ad-slot"}
          placement="inline"
          adFormat="fluid"
          style={{ margin: '20px 0' }}
        /> */}
        
        {/* Mock Ad for Testing - Remove when using real ads */}
        <MockAd placement="inline"/>

        <div className={styles.overviewSection}>
          <h3>Overall Score Comparison</h3>
          
          <div className={styles.scoreOverviewContainer}>
            <div className={styles.scoreSideBySide}>
              <div className={styles.scoreChartContainer}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={scoreData} 
                    margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis 
                      domain={[0, 100]} 
                      tickCount={6} 
                      label={{ value: 'Score', angle: -90, position: 'insideLeft', dx: -10 }}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}/100`, 'Score']}
                      contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: 'none' }}
                    />
                    <Bar 
                      dataKey="score" 
                      fill="#0077cc" 
                      radius={[4, 4, 0, 0]} 
                      barSize={60}
                      animationDuration={1500}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className={styles.contractorScoreCards}>
                {Object.entries(projectComparisonData.comparison.total_scores).map(([name, score], index) => (
                  <div key={index} className={styles.contractorScoreCard}>
                    <div className={styles.contractorScoreHeader}>
                      <div 
                        className={styles.contractorScoreAvatar}
                        style={{backgroundColor: contractorColors[name]}}
                      >
                        {getInitials(name)}
                      </div>
                      <h4>{name}</h4>
                    </div>
                    <div className={styles.contractorTotalScore}>
                      <div className={styles.scoreCircle}>
                        <span className={styles.scoreNumber}>{score}</span>
                        <span className={styles.scoreLabel}>/100</span>
                      </div>
                    </div>
                    <div className={styles.contractorScoreStrengths}>
                      <div className={styles.strengthTitle}>Top Strengths:</div>
                      <ul className={styles.strengthsList}>
                        {detailedScoreData
                          .filter(item => item[name] >= 9)  
                          .slice(0, 3)
                          .map((item, idx) => (
                            <li key={idx}>{item.category} ({item[name]}/10)</li>
                          ))
                        }
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.overviewSection}>
          <h3>Category Scores Comparison</h3>
          <p className={styles.categoryScoreDescription}>
            Explore how each contractor scores across different categories. 
            Click on a contractor in the legend to show/hide their data, or change the chart type below.
          </p>
          
          <div className={styles.chartControls}>
            <div className={styles.chartTypeSwitcher}>
              <button 
                className={`${styles.chartTypeButton} ${chartType === 'radar' ? styles.activeChartType : ''}`} 
                onClick={() => setChartType('radar')}
              >
                <i className="fas fa-chart-pie"></i> Radar
              </button>
              <button 
                className={`${styles.chartTypeButton} ${chartType === 'bar' ? styles.activeChartType : ''}`} 
                onClick={() => setChartType('bar')}
              >
                <i className="fas fa-chart-bar"></i> Bar
              </button>
              <button 
                className={`${styles.chartTypeButton} ${chartType === 'line' ? styles.activeChartType : ''}`} 
                onClick={() => setChartType('line')}
              >
                <i className="fas fa-chart-line"></i> Line
              </button>
              <button 
                className={`${styles.chartTypeButton} ${chartType === 'table' ? styles.activeChartType : ''}`} 
                onClick={() => setChartType('table')}
              >
                <i className="fas fa-table"></i> Table
              </button>
            </div>
            
            <div className={styles.contractorToggle}>
              <span className={styles.toggleLabel}>Toggle Contractors:</span>
              {Object.keys(contractorColors).map(contractor => (
                <div 
                  key={contractor} 
                  className={`${styles.contractorToggleItem} ${!activeContractors || !activeContractors[contractor] ? styles.inactiveContractor : ''}`}
                  onClick={() => toggleContractor(contractor)}
                >
                  <div 
                    className={styles.contractorColorSwatch} 
                    style={{backgroundColor: contractorColors[contractor]}}
                  ></div>
                  <span>{contractor}</span>
                  <i className={`fas ${!activeContractors || !activeContractors[contractor] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.radarChartContainer}>
            {chartType === 'radar' && (
              <ResponsiveContainer width="100%" height={500}>
                <RadarChart outerRadius={200} data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: '#333', fontSize: 13 }}
                    tickFormatter={(value) => value.length > 25 ? `${value.substring(0, 25)}...` : value}
                  />
                  <PolarRadiusAxis domain={[0, 10]} tickCount={6} />
                  {Object.keys(contractorColors).map(contractor => (
                    <Radar
                      key={contractor}
                      name={contractor}
                      dataKey={contractor}
                      stroke={activeContractors && activeContractors[contractor] ? contractorColors[contractor] : '#CBD5E1'}
                      fill={activeContractors && activeContractors[contractor] ? contractorColors[contractor] : '#CBD5E1'}
                      fillOpacity={activeContractors && activeContractors[contractor] ? 0.5 : 0.2}
                      animationDuration={1500}
                    />
                  ))}
                  <Legend 
                    onClick={(data) => toggleContractor(data.dataKey)}
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value, entry) => {
                      return (
                        <span style={{ 
                          color: activeContractors && activeContractors[value] ? '#334155' : '#94A3B8',
                          textDecoration: activeContractors && activeContractors[value] ? 'none' : 'line-through',
                          marginRight: '10px'
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(1)}/10`, '']}
                    contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'bar' && (
              <ResponsiveContainer width="100%" height={500}>
                <BarChart 
                  data={prepareCategoryBarData()} 
                  layout="vertical" 
                  margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 10]} tickCount={11} />
                  <YAxis 
                    dataKey="category" 
                    type="category" 
                    width={140} 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.length > 20 ? `${value.substring(0, 20)}...` : value} 
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                    formatter={(value) => [`${value.toFixed(1)}/10`, '']}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                  />
                  <Legend 
                    onClick={(data) => toggleContractor(data.dataKey)}
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={(value, entry) => {
                      return (
                        <span style={{ 
                          color: activeContractors && activeContractors[value] ? '#334155' : '#94A3B8',
                          textDecoration: activeContractors && activeContractors[value] ? 'none' : 'line-through',
                          marginRight: '10px'
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  {Object.keys(contractorColors).map(contractor => (
                    <Bar 
                      key={contractor} 
                      dataKey={contractor} 
                      fill={activeContractors && activeContractors[contractor] ? contractorColors[contractor] : '#CBD5E1'}
                      name={contractor}
                      animationDuration={1500}
                      barSize={24}
                      radius={[0, 4, 4, 0]}
                      fillOpacity={activeContractors && activeContractors[contractor] ? 1 : 0.3}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'line' && (
              <ResponsiveContainer width="100%" height={500}>
                <LineChart 
                  data={prepareCategoryBarData()} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    tick={{ fontSize: 12 }}
                    interval={0}
                    tickMargin={10}
                  />
                  <YAxis 
                    domain={[0, 10]} 
                    tickCount={11}
                    label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                    formatter={(value) => [`${value.toFixed(1)}/10`, '']}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                  />
                  <Legend 
                    onClick={(data) => toggleContractor(data.dataKey)}
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value, entry) => {
                      return (
                        <span style={{ 
                          color: activeContractors && activeContractors[value] ? '#334155' : '#94A3B8',
                          textDecoration: activeContractors && activeContractors[value] ? 'none' : 'line-through',
                          marginRight: '10px'
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  {Object.keys(contractorColors).map(contractor => (
                    <Line 
                      key={contractor} 
                      type="monotone" 
                      dataKey={contractor} 
                      stroke={activeContractors && activeContractors[contractor] ? contractorColors[contractor] : '#CBD5E1'}
                      name={contractor}
                      strokeWidth={3}
                      dot={{ r: 6, strokeWidth: 2, fill: '#fff' }}
                      activeDot={{ r: 8, strokeWidth: 0 }}
                      animationDuration={1500}
                      strokeDasharray={activeContractors && activeContractors[contractor] ? "0" : "5 5"}
                      strokeOpacity={activeContractors && activeContractors[contractor] ? 1 : 0.5}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'table' && (
              <div className={styles.comparisonTable}>
                <table>
                  <thead>
                    <tr>
                      <th className={styles.categoryCol}>Category</th>
                      {Object.keys(contractorColors).map(contractor => (
                        <th key={contractor} className={styles.scoreCol}>
                          <div className={styles.contractorHeader}>
                            <div 
                              className={styles.contractorColorSquare} 
                              style={{
                                backgroundColor: activeContractors && activeContractors[contractor] 
                                  ? contractorColors[contractor] 
                                  : '#CBD5E1'
                              }}
                            ></div>
                            <span style={{ 
                              color: activeContractors && activeContractors[contractor] ? '#1e293b' : '#94A3B8',
                              textDecoration: activeContractors && activeContractors[contractor] ? 'none' : 'line-through'
                            }}>
                              {contractor}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projectComparisonData.comparison.scorecard.categories.map((category, index) => (
                      <tr key={index}>
                        <td className={styles.categoryCell}>{category}</td>
                        {Object.keys(contractorColors).map(contractor => {
                          const score = projectComparisonData.comparison.scorecard[contractor][index];
                          const isActive = activeContractors && activeContractors[contractor];
                          return (
                            <td key={`${contractor}-${index}`} className={styles.scoreCell}>
                              <div className={styles.scoreSliderContainer}>
                                <div className={styles.scoreSliderTrack}>
                                  <div 
                                    className={styles.scoreSliderFill} 
                                    style={{
                                      width: `${score * 10}%`, 
                                      backgroundColor: isActive ? contractorColors[contractor] : '#CBD5E1',
                                      opacity: isActive ? 1 : 0.4
                                    }}
                                  ></div>
                                </div>
                                <span className={styles.scoreValue} style={{
                                  color: isActive ? '#1e293b' : '#94A3B8',
                                  fontWeight: isActive ? '600' : '400'
                                }}>
                                  {score}/10
                                </span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className={styles.categoryLegend}>
              <div className={styles.legendTitle}>Category Definitions:</div>
              <div className={styles.legendItems}>
                <div className={styles.legendItem}>
                  <span className={styles.legendItemName}>Scope & Detailing:</span>
                  <span className={styles.legendItemDesc}>Thoroughness of proposal scope and specifications</span>
                </div>
                <div className={styles.legendItem}>
                  <span className={styles.legendItemName}>Cost Transparency:</span>
                  <span className={styles.legendItemDesc}>Clarity in pricing, allowances, and potential cost changes</span>
                </div>
                <div className={styles.legendItem}>
                  <span className={styles.legendItemName}>Legal/Insurance:</span>
                  <span className={styles.legendItemDesc}>Coverage, warranties, and legal protections provided</span>
                </div>
                <div className={styles.legendItem}>
                  <span className={styles.legendItemName}>Project Management:</span>
                  <span className={styles.legendItemDesc}>Scheduling, communication, and project oversight</span>
                </div>
                <div className={styles.legendItem}>
                  <span className={styles.legendItemName}>Client Collaboration:</span>
                  <span className={styles.legendItemDesc}>Flexibility and responsiveness to client input</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.overviewSection}>
          <h3>Cost Breakdown Analysis</h3>
          
          <div className={styles.costBreakdownSection}>
            <div className={styles.costChartContainer}>
              <h4>Total Project Costs</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={costData} 
                  margin={{ top: 20, right: 30, bottom: 30, left: 40 }}
                  barGap={30}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: '#475569', fontSize: 13 }}
                    tickMargin={10}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                    tick={{ fill: '#475569', fontSize: 13 }}
                    label={{ 
                      value: 'Total Cost', 
                      angle: -90, 
                      position: 'insideLeft', 
                      style: { textAnchor: 'middle', fill: '#475569', fontSize: 13 } 
                    }}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Total Cost']}
                    contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                    labelStyle={{ fontWeight: 'bold', color: '#1e293b' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '15px' }} />
                  <Bar 
                    dataKey="cost" 
                    name="Project Cost" 
                    fill="#28a745" 
                    radius={[4, 4, 0, 0]} 
                    barSize={60}
                    animationDuration={1500}
                  >
                    {costData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={Object.values(contractorColors)[index % Object.values(contractorColors).length]} 
                      />
                    ))}
                    <LabelList 
                      dataKey="cost" 
                      position="top" 
                      formatter={(value) => `$${value.toLocaleString()}`}
                      style={{ fill: '#334155', fontWeight: '600', fontSize: '12px' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              <div className={styles.costAnalysisText}>
                <div className={styles.costDetailBox}>
                  <div className={styles.costDetailHeading}>
                    <i className="fas fa-calculator"></i>
                    <span>Price Difference:</span> 
                  </div>
                  <div className={styles.costDetailValue}>
                    ${Math.abs(costData[0].cost - costData[1].cost).toLocaleString()} ({Math.round((Math.max(...costData.map(d => d.cost)) - Math.min(...costData.map(d => d.cost))) / Math.min(...costData.map(d => d.cost)) * 100)}% variance)
                  </div>
                </div>
                
                <div className={styles.costDetailBox}>
                  <div className={styles.costDetailHeading}>
                    <i className="fas fa-balance-scale"></i>
                    <span>Value Analysis:</span>
                  </div>
                  <div className={styles.costDetailValue}>
                    The higher-priced contractor ({highestScoredContractor}) scores {highestScore} points, making the cost-per-point ${(Math.max(...costData.map(d => d.cost)) / highestScore).toFixed(2)}.
                  </div>
                </div>
                
                <div className={styles.costBreakdownDetails}>
                  <h5><i className="fas fa-chart-pie"></i> Cost Composition</h5>
                  <div className={styles.costBreakdownGrid}>
                    {(() => {
                      // Get contractor names from data
                      const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
                        .filter(key => key !== 'categories');
                      
                      if (contractorNames.length < 2) return (
                        <div className={styles.noCostBreakdown}>
                          <p>Not enough contractors to compare cost breakdowns.</p>
                      </div>
                      );
                      
                      // Get allowance categories from the actual JSON data
                      const contractor1 = projectComparisonData.proposals[0];
                      const contractor2 = projectComparisonData.proposals[1];
                      
                      // Find common allowance categories to compare
                      const allAllowanceCategories = new Set();
                      
                      // Collect all allowance categories from both contractors
                      if (contractor1?.details?.allowances) {
                        Object.keys(contractor1.details.allowances).forEach(key => 
                          allAllowanceCategories.add(key));
                      }
                      
                      if (contractor2?.details?.allowances) {
                        Object.keys(contractor2.details.allowances).forEach(key => 
                          allAllowanceCategories.add(key));
                      }
                      
                      // Convert to array and take the first 4 categories for display
                      const categoryKeys = Array.from(allAllowanceCategories).slice(0, 4);
                      
                      // Format the data for display
                      const costCategories = categoryKeys.map(key => {
                        const value1 = contractor1?.details?.allowances?.[key] || 0;
                        const value2 = contractor2?.details?.allowances?.[key] || 0;
                        
                        // Calculate percentage difference when both have values
                        let percentDiff = 0;
                        if (value1 > 0 && value2 > 0) {
                          percentDiff = Math.round((Math.max(value1, value2) - Math.min(value1, value2)) / 
                            Math.min(value1, value2) * 100);
                        } else if (value1 > 0 && value2 === 0) {
                          percentDiff = 100; // One has a value, the other doesn't
                        } else if (value1 === 0 && value2 > 0) {
                          percentDiff = 100; // One has a value, the other doesn't
                        }
                        
                        // Format the category name for display
                        const displayName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        
                        return {
                          name: displayName,
                          percentDiff,
                          values: {
                            [contractorNames[0]]: value1,
                            [contractorNames[1]]: value2
                          },
                          note: `Allowance for ${displayName.toLowerCase()}`
                        };
                      });
                      
                      return costCategories.map((category, index) => (
                        <div key={index} className={styles.costBreakdownCard}>
                      <div className={styles.costBreakdownCardHeader}>
                            <span>{category.name}</span>
                            <div className={styles.costPercentDiff}>+{category.percentDiff}%</div>
                      </div>
                      <div className={styles.costComparison}>
                            {contractorNames.slice(0, 2).map((contractor, i) => (
                              <div key={i} className={styles.costComparisonItem}>
                          <div className={styles.costComparisonLabel}>
                                  <div className={styles.contractorDot} style={{backgroundColor: contractorColors[contractor]}}></div>
                                  <span>{contractor}</span>
                          </div>
                                <div className={styles.costComparisonValue}>${category.values[contractor].toLocaleString()}</div>
                        </div>
                            ))}
                          </div>
                          <div className={styles.costBreakdownNote}>{category.note}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.allowanceChartContainer}>
              <h4>Allowance Breakdown by Category</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={prepareAllowanceComparisonData()} 
                  margin={{ top: 20, right: 30, bottom: 30, left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                    height={50}
                    tickMargin={10}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                    label={{ 
                      value: 'Allowance Amount', 
                      angle: -90, 
                      position: 'insideLeft', 
                      style: { textAnchor: 'middle', fill: '#475569', fontSize: 13 } 
                    }}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                    labelStyle={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  {Object.keys(contractorColors).map(contractor => (
                    <Bar 
                      key={contractor} 
                      dataKey={contractor} 
                      fill={activeContractors && activeContractors[contractor] ? contractorColors[contractor] : '#CBD5E1'}
                      fillOpacity={activeContractors && activeContractors[contractor] ? 1 : 0.3}
                      radius={[4, 4, 0, 0]}
                      barSize={35}
                      animationDuration={1500}
                    >
                      <LabelList 
                        dataKey={contractor} 
                        position="top" 
                        formatter={(value) => value > 0 ? `$${value.toLocaleString()}` : ''}
                        style={{ fill: '#334155', fontWeight: '500', fontSize: '11px' }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              
              <div className={styles.allowanceExplanation}>
                <h5><i className="fas fa-info-circle"></i> What are allowances?</h5>
                <p>Allowances are budgeted amounts set aside for specific items that you'll select later (like flooring, fixtures, etc.). If your selections cost more than the allowance, you'll pay the difference.</p>
                
                <div className={styles.allowanceTotals}>
                  <div className={styles.allowanceTotalBox}>
                    <div className={styles.allowanceTotalHeader}>
                      <div 
                        className={styles.allowanceColorDot} 
                        style={{backgroundColor: contractorColors[Object.keys(contractorColors)[0] || 'Rudloff']}}
                      ></div>
                      <span>
                        {projectComparisonData.proposals[0]?.name || 'Unknown Contractor'}
                      </span>
                    </div>
                    <div className={styles.allowanceTotalAmount}>
                      ${Object.values(projectComparisonData.proposals[0]?.details?.allowances || {})
                          .reduce((sum, val) => sum + val, 0)
                          .toLocaleString()}
                    </div>
                    <div className={styles.allowanceTotalItems}>
                      {Object.keys(projectComparisonData.proposals[0]?.details?.allowances || {}).length} allowance categories
                    </div>
                  </div>
                  
                  {projectComparisonData.proposals.length > 1 && (
                  <div className={styles.allowanceTotalBox}>
                    <div className={styles.allowanceTotalHeader}>
                      <div 
                        className={styles.allowanceColorDot} 
                          style={{backgroundColor: contractorColors[Object.keys(contractorColors)[1] || 'TJs']}}
                      ></div>
                        <span>
                          {projectComparisonData.proposals[1]?.name || 'Unknown Contractor'}
                        </span>
                    </div>
                    <div className={styles.allowanceTotalAmount}>
                        ${Object.values(projectComparisonData.proposals[1]?.details?.allowances || {})
                            .reduce((sum, val) => sum + val, 0)
                            .toLocaleString()}
                    </div>
                    <div className={styles.allowanceTotalItems}>
                        {Object.keys(projectComparisonData.proposals[1]?.details?.allowances || {}).length} allowance categories
                    </div>
                  </div>
                  )}
                </div>
                
                {(() => {
                  // Calculate allowance totals
                  const allowance1 = Object.values(projectComparisonData.proposals[0]?.details?.allowances || {})
                    .reduce((sum, val) => sum + val, 0);
                  const allowance2 = Object.values(projectComparisonData.proposals[1]?.details?.allowances || {})
                    .reduce((sum, val) => sum + val, 0);
                  
                  // Only show if there are at least 2 contractors with allowances
                  if (allowance1 > 0 && allowance2 > 0) {
                    const difference = Math.abs(allowance1 - allowance2);
                    return (
                <div className={styles.allowanceWarning}>
                  <i className="fas fa-exclamation-triangle"></i>
                        <p>The ${difference.toLocaleString()} difference in allowances is significant and may impact your final cost. Lower allowances often lead to more change orders during construction.</p>
                </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
          
          <div className={styles.valueAnalysisContainer}>
            <h4>Key Cost Factors to Consider</h4>
            <div className={styles.valueFactors}>
              <div className={styles.valueFactor}>
                <div className={styles.valueFactorIcon}>
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className={styles.valueFactorContent}>
                  <h5>Warranty & Liability</h5>
                  <p>
                    {projectComparisonData.proposals.map((proposal, index) => (
                      proposal.details.warranty ? 
                        <span key={index}>{proposal.name} provides {proposal.details.warranty.substring(0, 100)}... </span> :
                        <span key={index}>{proposal.name} doesn't explicitly mention warranty terms. </span>
                    ))}
                  </p>
                </div>
              </div>
              
              <div className={styles.valueFactor}>
                <div className={styles.valueFactorIcon}>
                  <i className="fas fa-pencil-ruler"></i>
                </div>
                <div className={styles.valueFactorContent}>
                  <h5>Design Services</h5>
                  <p>
                    {projectComparisonData.proposals.some(p => p.details.design_services) ? (
                      <>
                        {projectComparisonData.proposals.find(p => p.details.design_services)?.name} 
                        includes architectural plans and design services
                        {projectComparisonData.proposals.find(p => 
                          p.details.allowances?.architect_fees)?.details.allowances.architect_fees ? 
                          ` (valued at $${projectComparisonData.proposals.find(p => 
                            p.details.allowances?.architect_fees).details.allowances.architect_fees.toLocaleString()})` : 
                          ''}
                        , making it more comprehensive for complex projects.
                      </>
                    ) : (
                      "Design services vary between contractors. Check each proposal for specifics."
                    )}
                  </p>
                </div>
              </div>
              
              <div className={styles.valueFactor}>
                <div className={styles.valueFactorIcon}>
                  <i className="fas fa-toolbox"></i>
                </div>
                <div className={styles.valueFactorContent}>
                  <h5>Materials Quality</h5>
                  <p>
                    {(() => {
                      // Find totals for all allowances
                      const allowanceTotals = projectComparisonData.proposals.map(proposal => {
                        let total = 0;
                        if (proposal.details.allowances) {
                          total = Object.values(proposal.details.allowances).reduce((sum, value) => sum + value, 0);
                        }
                        return { name: proposal.name, total };
                      });
                      
                      // Sort by total allowance (highest first)
                      allowanceTotals.sort((a, b) => b.total - a.total);
                      
                      // If we have at least 2 contractors to compare
                      if (allowanceTotals.length >= 2) {
                        return `${allowanceTotals[0].name} has higher material allowances overall ($${allowanceTotals[0].total.toLocaleString()} vs $${allowanceTotals[1].total.toLocaleString()}), potentially indicating higher-quality materials and finishes.`;
                      } else if (allowanceTotals.length === 1) {
                        return `${allowanceTotals[0].name} has material allowances totaling $${allowanceTotals[0].total.toLocaleString()}.`;
                      } else {
                        return "Material allowances are not clearly specified in the proposals.";
                      }
                    })()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.overviewSection}>
          <h3>Timeline Comparison</h3>
          
          <div className={styles.timelineSection}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={timelineData} 
                margin={{ top: 20, right: 30, bottom: 30, left: 70 }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit=" days" tickCount={8} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={65} 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value}
                />
                <Tooltip 
                  formatter={(value) => [`${value} days`, '']}
                  contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: 'none' }}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                {Object.keys(contractorColors).map(contractor => (
                  <Bar 
                    key={contractor} 
                    dataKey={contractor} 
                    fill={contractorColors[contractor]} 
                    radius={[0, 4, 4, 0]}
                    barSize={24}
                    animationDuration={1500}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            
            <div className={styles.timelineAnalysis}>
              {(() => {
                // Get contractor names from data
                const contractorNames = Object.keys(projectComparisonData.comparison.scorecard)
                  .filter(key => key !== 'categories');
                
                if (contractorNames.length < 2) return (
                  <div className={styles.noTimelineData}>
                    <p>Not enough contractors to compare timelines.</p>
                </div>
                );
                
                const timelineData = prepareTimelineData();
                
                // Calculate total days for each contractor
                const totalDays = {};
                contractorNames.forEach(name => {
                  totalDays[name] = timelineData.reduce((sum, item) => sum + (item[name] || 0), 0);
                });
                
                // Find percentage difference between first and second contractor
                const percentDiff = Math.round(
                  (totalDays[contractorNames[0]] - totalDays[contractorNames[1]]) / 
                  totalDays[contractorNames[1]] * 100
                );
                
                return (
                  <>
                    {contractorNames.map((name, index) => (
                      <div key={index} className={styles.timelineMetric}>
                <div className={styles.timelineMetricValue}>
                          {totalDays[name]} days
                </div>
                <div className={styles.timelineMetricLabel}>
                          {name} Estimated Timeline
                </div>
              </div>
                    ))}
              
              <div className={styles.timelineDifference}>
                <span className={styles.timelineDifferenceValue}>
                        {percentDiff}%
                </span>
                <span className={styles.timelineDifferenceLabel}>
                        Longer timeline for {contractorNames[0]}
                </span>
              </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        
        <div className={styles.overviewSection}>
          <h3>Scope Coverage Analysis</h3>
          
          <div className={styles.scopeCoverageContainer}>
            {projectComparisonData.proposals.map((proposal, index) => (
              <div key={index} className={styles.contractorScopeCard}>
                <div className={styles.contractorScopeHeader}>
                  <div 
                    className={styles.contractorScopeAvatar}
                    style={{backgroundColor: contractorColors[proposal.name.split(' ')[0]]}}
                  >
                    {getInitials(proposal.name)}
                  </div>
                  <div>
                    <h4>{proposal.name}</h4>
                    <div className={styles.contractorScopePrice}>${proposal.total_cost.toLocaleString()}</div>
                  </div>
                </div>
                
                <div className={styles.contractorScopeDetails}>
                  <div className={styles.scopeDetailsSection}>
                    <h5>Key Scope Elements</h5>
                    <ul className={styles.scopeDetailsList}>
                      {Object.entries(proposal.details)
                        .filter(([key]) => key !== 'allowances' && key !== 'exclusions' && key !== 'assumptions')
                        .slice(0, 5)
                        .map(([key, value], idx) => (
                          <li key={idx}>
                            <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                  
                  {proposal.details.exclusions && (
                    <div className={styles.scopeExclusionsSection}>
                      <h5>Notable Exclusions</h5>
                      <ul className={styles.scopeExclusionsList}>
                        {proposal.details.exclusions.slice(0, 3).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                        {proposal.details.exclusions.length > 3 && (
                          <li className={styles.moreItems}>
                            +{proposal.details.exclusions.length - 3} more exclusions
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className={styles.contractorProsConsContainer}>
                  <div className={styles.prosSection}>
                    <h5><i className="fas fa-plus-circle"></i> Pros</h5>
                    <ul className={styles.prosList}>
                      {proposal.pros.slice(0, 3).map((pro, idx) => (
                        <li key={idx}>{pro}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className={styles.consSection}>
                    <h5><i className="fas fa-minus-circle"></i> Cons</h5>
                    <ul className={styles.consList}>
                      {proposal.cons.slice(0, 3).map((con, idx) => (
                        <li key={idx}>{con}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContractorsTab = () => {
    // If not premium and trying to access premium tab
    if (!isPremiumUnlocked) {
      return (
        <div className={styles.premiumBlocker}>
          <div className={styles.premiumBlockerContent}>
            <i className="fas fa-lock"></i>
            <h3>Premium Content</h3>
            <p>Unlock detailed contractor analysis, scope details, and pros & cons.</p>
            <button className={styles.unlockButton} onClick={() => setShowUpgradeModal(true)}>
              <i className="fas fa-unlock"></i> Unlock Full Comparison
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.contractorsTab}>
        <div className={styles.contractorsList}>
          {projectComparisonData.proposals.map((proposal, index) => (
            <div 
              key={index} 
              className={`${styles.contractorCard} ${selectedProposalIndex === index ? styles.selected : ''}`}
              onClick={() => setSelectedProposalIndex(index)}
            >
              <div className={styles.contractorHeader}>
                <div className={styles.contractorAvatar}>
                  {getInitials(proposal.name)}
                </div>
                <div className={styles.contractorInfo}>
                  <h3>{proposal.name}</h3>
                  <p className={styles.contractorCost}>${proposal.total_cost.toLocaleString()}</p>
                </div>
              </div>
              <div className={styles.contractorScope}>
                <strong>Scope:</strong> {proposal.scope}
              </div>
            </div>
          ))}
        </div>

        {selectedProposalIndex !== null && (
          <div className={styles.contractorDetails}>
            <h3>{projectComparisonData.proposals[selectedProposalIndex].name} Details</h3>
            
            <div className={styles.detailsSection}>
              <h4>Included in Proposal</h4>
              <div className={styles.detailsGrid}>
                {Object.entries(projectComparisonData.proposals[selectedProposalIndex].details)
                  .filter(([key]) => key !== 'allowances' && key !== 'exclusions')
                  .map(([key, value], index) => (
                    <div key={index} className={styles.detailItem}>
                      <div className={styles.detailKey}>{key.replace(/_/g, ' ')}</div>
                      <div className={styles.detailValue}>{value}</div>
                    </div>
                  ))
                }
              </div>
            </div>

            {projectComparisonData.proposals[selectedProposalIndex].details.allowances && (
              <div className={styles.allowancesSection}>
                <h4>Allowances</h4>
                <div className={styles.allowancesGrid}>
                  {Object.entries(projectComparisonData.proposals[selectedProposalIndex].details.allowances).map(([key, value], index) => (
                    <div key={index} className={styles.allowanceItem}>
                      <div className={styles.allowanceKey}>{key.replace(/_/g, ' ')}</div>
                      <div className={styles.allowanceValue}>${value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {projectComparisonData.proposals[selectedProposalIndex].details.exclusions && (
              <div className={styles.exclusionsSection}>
                <h4>Exclusions</h4>
                <ul className={styles.exclusionsList}>
                  {projectComparisonData.proposals[selectedProposalIndex].details.exclusions.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.prosCons}>
              <div className={styles.pros}>
                <h4>Pros</h4>
                <ul>
                  {projectComparisonData.proposals[selectedProposalIndex].pros.map((pro, index) => (
                    <li key={index}>{pro}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.cons}>
                <h4>Cons</h4>
                <ul>
                  {projectComparisonData.proposals[selectedProposalIndex].cons.map((con, index) => (
                    <li key={index}>{con}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuestionsTab = () => {
    // If not premium and trying to access premium tab
    if (!isPremiumUnlocked) {
      return (
        <div className={styles.premiumBlocker}>
          <div className={styles.premiumBlockerContent}>
            <i className="fas fa-lock"></i>
            <h3>Premium Content</h3>
            <p>Unlock custom questions to ask contractors before making your final decision.</p>
            <button className={styles.unlockButton} onClick={() => setShowUpgradeModal(true)}>
              <i className="fas fa-unlock"></i> Unlock Questions Package
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.questionsTab}>
        <div className={styles.questionsIntro}>
          <h2>Questions to Ask Before Deciding</h2>
          <p>Use these questions to get clarity from contractors and make a more informed decision.</p>
        </div>
        
        <div className={styles.questionCategories}>
          {projectComparisonData.homeowner_questions_to_ask.map((category, index) => (
            <div key={index} className={styles.questionCategory}>
              <h3>{category.category}</h3>
              <ul className={styles.questionsList}>
                {category.questions.map((question, qIndex) => (
                  <li key={qIndex}>{question}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderUpgradeModal = () => {
    if (!showUpgradeModal) return null;
    
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.upgradeModal}>
          <div className={styles.upgradeModalHeader}>
            <h2>Unlock Premium Features</h2>
            <button className={styles.closeModal} onClick={() => setShowUpgradeModal(false)}>×</button>
          </div>
          
          <div className={styles.upgradeModalContent}>
            <p className={styles.upgradeIntro}>Get deeper insights to make the best decision on your contractor selection.</p>
            
            <div className={styles.planOptions}>
              {premiumPlans.map(plan => (
                <div 
                  key={plan.id} 
                  className={`
                    ${styles.planCard} 
                    ${selectedPlan?.id === plan.id ? styles.selectedPlan : ''} 
                    ${plan.popular ? styles.popularPlan : ''}
                  `}
                  onClick={() => setSelectedPlan(plan)}
                >
                  {plan.popular && <div className={styles.popularBadge}>Most Popular</div>}
                  <div className={styles.planIcon}>
                    <i className={`fas ${plan.icon}`}></i>
                  </div>
                  <h3>{plan.name}</h3>
                  <div className={styles.planPrice}>${plan.price}</div>
                  <ul className={styles.planFeatures}>
                    {plan.features.map((feature, index) => (
                      <li key={index}><i className="fas fa-check"></i> {feature}</li>
                    ))}
                  </ul>
                  <button 
                    className={`${styles.selectPlanButton} ${selectedPlan?.id === plan.id ? styles.selectedButton : ''}`}
                  >
                    {selectedPlan?.id === plan.id ? 'Selected' : 'Select'}
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.upgradeModalFooter}>
            <button className={styles.cancelButton} onClick={() => setShowUpgradeModal(false)}>
              Cancel
            </button>
            <button 
              className={styles.purchaseButton} 
              disabled={!selectedPlan}
              onClick={handleUnlock}
            >
              {selectedPlan ? `Purchase for $${selectedPlan.price}` : 'Select a plan'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add function to handle comparison deletion
  const handleDeleteComparison = async () => {
    if (!comparisonId) {
      setToast({
        type: 'error',
        message: 'No comparison ID available to delete'
      });
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const response = await proposalComparisonService.deleteComparison(comparisonId);
      
      if (response.data && response.data.status === 'success') {
        setToast({
          type: 'success',
          message: 'Comparison deleted successfully'
        });
        
        // Navigate back to dashboard after successful deletion
        setTimeout(() => {
          navigate('/homeowner/dashboard');
        }, 1500);
      } else {
        throw new Error('Failed to delete comparison');
      }
    } catch (error) {
      console.error('Error deleting comparison:', error);
      setToast({
        type: 'error',
        message: `Delete failed: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Add delete confirmation modal
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.deleteModal}>
          <div className={styles.deleteModalHeader}>
            <h2>Delete Comparison</h2>
            <button className={styles.closeModal} onClick={() => setShowDeleteModal(false)}>×</button>
          </div>
          
          <div className={styles.deleteModalContent}>
            <div className={styles.deleteWarningIcon}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <p>Are you sure you want to delete this comparison?</p>
            <p className={styles.deleteWarning}>This action cannot be undone. All associated files will also be deleted.</p>
          </div>
          
          <div className={styles.deleteModalFooter}>
            <button 
              className={styles.cancelButton} 
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              className={styles.deleteButton} 
              onClick={handleDeleteComparison}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className={styles.buttonSpinner}></div>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i> Delete Permanently
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.comparisonViewContainer}>
      {renderUpgradeModal()}
      {renderFilePreview()}
      {renderDeleteModal()}
      {toast && <Toast 
        type={toast.type} 
        message={toast.message} 
        onClose={() => setToast(null)}
      />}
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading comparison data...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <i className="fas fa-exclamation-circle"></i>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/homeowner/dashboard')} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>
      ) : !projectComparisonData ? (
        <div className={styles.errorContainer}>
          <i className="fas fa-exclamation-circle"></i>
          <h3>No Data Available</h3>
          <p>No comparison data available for this project.</p>
          <button onClick={() => navigate('/homeowner/dashboard')} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>
      ) : (
        <>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/homeowner/dashboard')}>
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
        <h1>Proposal Comparison</h1>
            <div className={styles.headerActions}>
              {isPremiumUnlocked ? (
          <div className={styles.premiumBadge}>
            <i className="fas fa-crown"></i> Premium
          </div>
              ) : (
                <button 
                  className={styles.unlockPremiumButton} 
                  onClick={() => setShowUpgradeModal(true)}
                  title="Unlock Premium Features"
                >
                  <i className="fas fa-crown"></i> Unlock Premium Access
                </button>
              )}
              <button 
                className={styles.deleteComparisonButton} 
                onClick={() => setShowDeleteModal(true)}
                title="Delete Comparison"
              >
                <i className="fas fa-trash-alt"></i> Delete
              </button>
            </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tabButton} ${activeTab === 'preview' ? styles.active : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            <i className="fas fa-eye"></i> Preview
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-chart-bar"></i> Comparison
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'contractors' ? styles.active : ''}`}
            onClick={() => setActiveTab('contractors')}
          >
            <i className="fas fa-hard-hat"></i> Contractors
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'questions' ? styles.active : ''}`}
            onClick={() => setActiveTab('questions')}
          >
            <i className="fas fa-question-circle"></i> Questions
          </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'files' ? styles.active : ''}`}
                onClick={() => setActiveTab('files')}
              >
                <i className="fas fa-file-alt"></i> Files
          </button>
        </div>
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'preview' && renderPreviewTab()}
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'contractors' && renderContractorsTab()}
        {activeTab === 'questions' && renderQuestionsTab()}
            {activeTab === 'files' && renderFilesTab()}
      </div>
        </>
      )}
    </div>
  );
}

export default ProposalComparisonView; 